/**
 * Where the two write paths meet.
 *
 * `@morph/runtime` owns the permission boundary and speaks in intents, grant
 * ids and undo scopes. `@morph/store` owns durability and speaks in
 * authorities, per-object grants and instructions. Neither should learn the
 * other's vocabulary — the runtime must stay renderable without a store, and
 * the store must stay usable without a DOM — so the translation lives here, in
 * the one place that already depends on both.
 *
 * Three rules this file exists to hold, none of which either side can hold
 * alone:
 *
 *  1. **The authority comes from the spec that was rendered.** A lens id is
 *     looked up against the specs this session actually painted; a caller never
 *     passes a spec in. Authorising against a spec the user is not looking at
 *     would let a stale — or invented — layout name a write.
 *  2. **Every intent is re-checked here.** `checkIntent` already ran in the
 *     host; it runs again against the grant set registered at lens install,
 *     because "the caller checked" is not a boundary. It is the same function
 *     on both sides rather than two that must agree.
 *  3. **All or nothing.** Grants are minted for every intent first, and the
 *     transaction is applied only if every one of them succeeded. A half-landed
 *     edit is a worse outcome than a refused one: the screen and the store
 *     disagree and nothing says which is right.
 */

import { ROLE_KIND, type RelationMutation, type Role, type TLayoutSpec } from '@morph/spec';
import {
  checkIntent, nextTxnId, scopeContains,
  type Commit, type EditIntent, type EditStore, type EditTransaction,
  type EditValue, type GrantSet, type ObjectHandle, type PropertyPatch,
  type Refusal, type RefusalCode, type UndoScopeId, type WriteOutcome,
} from '@morph/runtime';
import {
  CLEAR, isRefusal, rawOf,
  type AnyGrant, type BindingAuthority, type EditorAuthority, type Instruction,
  type JournalId, type ManifestRef, type ObjectId, type ObjectReader, type ObjectStore,
  type Refusal as StoreRefusal, type Value,
} from '@morph/store';

export interface EditBridgeDeps {
  readonly store: ObjectStore;
  /** The editor door. Undo only: a binding write restores nothing on its own —
   *  the mutation vocabulary cannot say "make this slot absent again" — so
   *  0007's single undo stack has to reach the other authority. */
  readonly editor: EditorAuthority;
  /** Read at call time, never captured. Adding a property mints a NEW manifest
   *  ref, and a captured one would authorise against a slot map that no longer
   *  describes what is on screen. */
  manifest(): ManifestRef;
  /** The spec a lens id was painted from. */
  specOf(lens: string): TLayoutSpec | undefined;
  /** Handles are per-session names minted by the app; the store never sees one
   *  and the DOM never sees an object id. */
  objectOf(handle: ObjectHandle): ObjectId | undefined;
  /**
   * Re-check whether a readable role is derived inside the exact snapshot that
   * plans the write. A render-pass restriction alone races another tab
   * migrating the object and retiring its scalar before this transaction.
   */
  isDerivedRole?(
    reader: ObjectReader, object: ObjectId, manifest: ManifestRef, role: Role,
  ): Promise<boolean>;
  /**
   * A write landed.
   *
   * `focus` is where the caret was, taken from the INTENT rather than left for
   * the caller to read off `document.activeElement`. By the time this runs the
   * store has already told its subscribers, a repaint may already have replaced
   * the element the caret was in, and an anchor read from the DOM at that point
   * is gone — which is how "the caret comes back after a commit" fails silently
   * and takes the undo chord with it, since the chord is only listened for
   * inside the render root.
   */
  onWrote(focus?: {
    readonly object: ObjectHandle; readonly node: number; readonly role: Role;
  }): void;
  /**
   * May this role point at this object?
   *
   * The picker renders only declared targets, and until this existed that was
   * the ONLY place the declaration was consulted — so the constraint lived in
   * the markup. @Codex, reviewing #119: *"if the DOM is modified to add an
   * option containing another live session handle … the same invalid
   * cross-domain relation this change is intended to prevent can still be
   * written through manipulated markup."*
   *
   * Correct, and the reason it is worth fixing is not the threat model — this
   * is a local-first app and the person editing the DOM owns the objects. It is
   * that a domain enforced only while drawing is not a domain, it is a default.
   * Every other authority in this bridge — the grant, the epoch, the manifest —
   * is checked here rather than trusted from the page, and this was the one
   * that was not.
   *
   * Optional so a host that declares no target scopes is unchanged: undefined
   * means "unconstrained", which is what `attachCandidates` already does when a
   * role declares nothing.
   */
  targetInScope?(role: Role, target: ObjectId): Promise<boolean>;
  // `role` is the GRANT's, resolved inside the bridge. A caller cannot choose
  // which domain is consulted, and cannot switch the check off by not naming
  // one.
}

const say = (code: RefusalCode, message: string): Refusal => ({ code, message });

/** A store refusal, in the runtime's words.
 *
 *  Only the codes that mean the same thing on both sides are mapped. Everything
 *  else keeps the store's own message rather than being flattened into a code
 *  that would send a caller looking in the wrong place. */
function fromStore(r: StoreRefusal): Refusal {
  switch (r.code) {
    case 'read-only-binding':   return say('no-grant', r.message);
    case 'undeclared-mutation': return say('mutation-undeclared', r.message);
    case 'unknown-slot':        return say('no-property-for-role', r.message);
    case 'ambiguous-role':      return say('no-property-for-role', r.message);
    case 'kind-mismatch':       return say('value-not-parsable', r.message);
    case 'stale':               return say('stale-epoch', r.message);
    default:                    return say('object-not-writable', r.message);
  }
}

/**
 * Which slot in the authority carries a role.
 *
 * The IR does not carry slot names — `compileSpec` keeps role, direction and
 * mutation and drops the slot — so a grant arriving from the renderer names a
 * NODE and a role, while the store's authority is keyed by slot. Recovering one
 * from the other is safe exactly while a role occupies one writable slot, which
 * is what the baseline generator emits.
 *
 * When it does not, the role is dropped rather than guessed at: writing into
 * whichever slot happened to be found first is a write to a field the user was
 * not editing, which is the failure 0007 calls data loss rather than a bug.
 */
function slotsByRole(authority: BindingAuthority): Map<Role, string> {
  const byRole = new Map<Role, string>();
  const ambiguous = new Set<Role>();
  for (const [slot, binding] of authority.writable) {
    if (byRole.has(binding.role)) { ambiguous.add(binding.role); continue; }
    byRole.set(binding.role, slot);
  }
  for (const role of ambiguous) byRole.delete(role);
  return byRole;
}

/**
 * A granted mutation plus a typed value, as one instruction — or a refusal.
 *
 * `Instruction` welds each grant type to its payload, so this is where a value
 * the runtime parsed becomes a value the store will accept, and where the two
 * type systems are reconciled once instead of at every call site.
 */
function instructionFor(
  grant: AnyGrant, role: Role, value: EditValue,
): Instruction | Refusal {
  const kind = ROLE_KIND[role];

  switch (grant.mutation) {
    case 'set-value':
      if (value.t === 'text') return { mutation: 'set-value', grant, value: { k: 'text', v: value.v } };
      if (value.t === 'number') return { mutation: 'set-value', grant, value: { k: 'number', v: value.v } };
      // Emptying returns the slot to `empty`. It does NOT make it `absent`,
      // and the two are different operations in the store — `clear` against
      // `drop`, named four lines apart on the editor door.
      //
      // This branch used to refuse every kind but text, arguing that nothing in
      // the closed mutation set makes a slot absent again and that shape
      // changes are the editor's. True, and about a different act: nothing here
      // asks for absence. @Codex ruled it on task 0096 — *"empty is a property
      // state, not a schema mutation"* — and the refusal turned out to be
      // downstream of a TYPE rather than a policy, since `Instruction`'s scalar
      // payloads had no inhabitant meaning "no value" until `CLEAR` existed.
      //
      // Text is no longer the exception that writes `''`. Storing the empty
      // string when the runtime said the property was CLEARED is a different
      // fact recorded under the same name: `normalizeValue` is explicit that an
      // emptied cell means "this object has no such property", and a `''` slot
      // still reads as `set`.
      if (value.t === 'empty') return { mutation: 'set-value', grant, value: CLEAR };
      return say('mutation-undeclared', 'Not saved — that value is not what this field writes.');

    case 'set-date':
      if (value.t === 'date') return { mutation: 'set-date', grant, value: value.v };
      if (value.t === 'empty') return { mutation: 'set-date', grant, value: CLEAR };
      return say('value-not-parsable',
        'Not saved — a date field takes a full date, as 2026-08-20.');

    case 'set-enum':
      if (value.t === 'enum') return { mutation: 'set-enum', grant, value: value.v };
      // The one this task began with. Every enum in the product was a one-way
      // door: settable, never unsettable, on a store that has always had a
      // `clear` for exactly this.
      if (value.t === 'empty') return { mutation: 'set-enum', grant, value: CLEAR };
      return say('mutation-undeclared', 'Not saved — this field is chosen from, not typed into.');

    // Both positional mutations resolve and refuse precisely, and neither can
    // succeed: no role in the closed vocabulary carries an ordinal, so there is
    // nowhere for a drag to write. `toggle` fails from the other side — there is
    // no boolean role for a checkbox to bind to.
    case 'toggle':
    case 'reorder-within':
    case 'move-between-groups':
      return say('mutation-undeclared',
        `Not saved — "${grant.mutation}" has no property to write to in this vocabulary yet.`);
    // A relation mutation is not a value write and never reaches this bridge:
    // its payload is an opaque relation-target handle, its gesture is attach or
    // detach, and decision 0016 puts it behind a node slot's `mutations`
    // declaration rather than a binding. Refused here so the seam says what it
    // is rather than falling through to a value it does not have.
    case 'link':
    case 'unlink':
      return say('mutation-undeclared',
        `Not saved — "${grant.mutation}" is a relation mutation and does not travel `
        + 'through a value edit.');
  }
}

/** What one write changed, read from the store on both sides of the commit.
 *  The DOM is never the authority for either half. */
async function readRole(
  store: ObjectReader, manifest: ManifestRef, id: ObjectId, role: Role,
): Promise<unknown> {
  const object = await store.get(id, { manifest });
  const values = object?.roles.get(role);
  return values && values[0] !== undefined ? rawOf(values[0]) : undefined;
}

/** One commit that touched nothing, for undo and redo — the host reads only
 *  `ok` from those, and inventing patches it will not look at would be a lie
 *  about what the store reported. */
const nothingCommit = (scope: UndoScopeId): Commit =>
  ({ txn: nextTxnId(), scope, at: Date.now(), patches: [] });

interface HistoryEntry {
  readonly journal: JournalId;
  readonly scope: UndoScopeId;
  /** The commit that undid this one. Present iff its effect is currently off. */
  undoneBy?: JournalId;
  /** WHEN it was undone, not just that it was.
   *
   *  Redo is the inverse of undo, so it has to be LIFO against the undos —
   *  which is not the same as any order `history` itself is in. Scanning
   *  newest-first for an entry carrying `undoneBy` found the entry undone
   *  FIRST, because undo walks newest→oldest: undoing 'a' then 'b' and redoing
   *  twice gave back 'b' then 'a' and finished on the wrong value. This counter
   *  is the undo order itself, so no scan direction has to stand in for it. */
  undoneAt?: number;
}

/**
 * One relation gesture, as the demo asks for it.
 *
 * Deliberately NOT part of `EditStore`. That interface is the value-write
 * contract the runtime's `EditHost` drives from keystrokes and drags; a
 * relation gesture is a button press the app routes itself, exactly as it
 * routes `.m-invoke`. Widening `EditStore` would put a payload the host has no
 * gesture for into the interface every host must implement.
 */
interface RelateSubject {
  readonly lens: string;
  /** IR index, off `data-morph-n`. Names the grant. */
  readonly node: number;
  /** The node slot's SPEC id, off the IR. Names the store's authority — a node
   *  slot has no binding and therefore no slot name. */
  readonly specId: string;
  /** The SOURCE object: the one whose relation set is changing. */
  readonly object: ObjectHandle;
}

/**
 * Attach: the object a picker chose, and nothing else.
 *
 * `token?: never` is load-bearing rather than decorative. @Codex: *"make
 * illegal shapes unrepresentable where possible, with a runtime refusal at the
 * boundary as defense in depth."* The shape that had to become unrepresentable
 * is a link carrying BOTH — because `relate` resolved `token ?? target`, so
 * such a request validated the target and wrote the token: a check and a write
 * about two different objects, which is worse than no check because the
 * refusal path never runs and the caller is told it worked.
 */
export interface LinkRequest extends RelateSubject {
  readonly mutation: 'link';
  readonly target: ObjectId;
  readonly token?: never;
}

/**
 * Detach: the occurrence token the renderer carried out of projection.
 *
 * A token names an EXISTING edge, which only detach has. The symmetry is not
 * cosmetic — an unlink carrying a target would be naming an object where the
 * gesture needs an occurrence, and two references to the same object are two
 * occurrences the store must be able to tell apart.
 */
export interface UnlinkRequest extends RelateSubject {
  readonly mutation: 'unlink';
  readonly token: string;
  readonly target?: never;
}

/**
 * One relation gesture, discriminated by its mutation.
 *
 * A union rather than one interface with two optional fields, so the compiler
 * refuses the mixed shapes instead of the boundary having to catch them. The
 * runtime refusals below still exist: this is a demo host, a caller can be
 * JavaScript or a cast, and a type is not a boundary.
 */
export type RelateRequest = LinkRequest | UnlinkRequest;

export interface RelationStore extends EditStore {
  relate(req: RelateRequest): Promise<WriteOutcome>;
}

export function createEditBridge(deps: EditBridgeDeps): RelationStore {
  const installed = new Map<string, GrantSet>();
  /**
   * Authorities, held BY THE SPEC OBJECT they were minted from.
   *
   * Rule 1 at the top of this file says *"the authority comes from the spec
   * that was rendered"* — and the cache was keyed by lens and manifest,
   * neither of which changes when a lens is regenerated. So a spec that
   * reshaped under a stable lens id authorised against the previous one: a
   * claim in a header comment that the code one screen below did not keep.
   *
   * Keyed on the spec OBJECT, because the question is *"is this the same spec
   * we authorised"* and identity answers exactly that. A regenerated spec is a
   * new object and mints a new authority; the same spec painted twice reuses
   * one.
   *
   * ## Weak all the way down, which the first version was not
   *
   * That version put a `WeakMap` on the spec, used the name it returned as part
   * of a STRING key into an ordinary `Map`, and claimed in this comment that
   * *"entries die with the spec, so nothing accumulates."* @Codex:
   *
   * > those entries do not die with the spec as the comment claims — the weak
   * > map forgets only the object-to-name association, while `authorities`
   * > permanently retains every string-keyed value … a long editing session
   * > therefore grows this cache once per write/repaint cycle.
   *
   * Correct, and the same failure this branch keeps turning up: **a comment
   * describing a property the code does not have.** A weak reference that hands
   * its key to a strong map is a strong map. The value hangs off the spec now,
   * so when the render loop drops a regenerated spec its authority goes with
   * it — and the sentence above is true because nothing else holds the entry.
   *
   * The inner map is by manifest: extending a manifest re-points what a slot
   * means, so an authority cached across that change would grant a write
   * against the old slot map.
   */
  const authorities = new WeakMap<object, Map<string, BindingAuthority>>();
  const history: HistoryEntry[] = [];
  let undoClock = 0;

  async function authorityFor(lens: string): Promise<BindingAuthority | Refusal> {
    const spec = deps.specOf(lens);
    if (!spec) return say('grant-unknown', 'Not saved — this lens is no longer the one on screen.');
    const manifest = deps.manifest();
    let perSpec = authorities.get(spec as object);
    const cached = perSpec?.get(String(manifest));
    if (cached) return cached;
    const authority = await deps.store.authorize(spec, manifest);
    if (isRefusal(authority)) return fromStore(authority);
    if (!perSpec) { perSpec = new Map(); authorities.set(spec as object, perSpec); }
    perSpec.set(String(manifest), authority);
    return authority;
  }

  async function apply(txn: EditTransaction): Promise<WriteOutcome> {
    const first: EditIntent | undefined = txn.intents[0];
    if (!first) return { ok: true, commit: nothingCommit(txn.scope) };

    const grants = installed.get(first.lens);
    if (!grants) {
      return { ok: false, refusal: say('grant-unknown', 'Not saved — this lens is not installed.') };
    }
    for (const intent of txn.intents) {
      // Rule 2. The host already ran this; a store that trusts its caller is
      // not a boundary, and running the SAME function is what stops the two
      // sides drifting into disagreeing about what was permitted.
      const refusal = checkIntent(intent, grants);
      if (refusal) return { ok: false, refusal };
    }

    const authority = await authorityFor(first.lens);
    if ('code' in authority) return { ok: false, refusal: authority };
    const slotOf = slotsByRole(authority);
    const manifest = authority.manifest;

    const targets: { intent: EditIntent; id: ObjectId }[] = [];
    for (const intent of txn.intents) {
      const id = deps.objectOf(intent.object);
      if (!id) {
        return { ok: false, refusal: say('object-not-writable',
          'Not saved — this row is from a render that is no longer on screen.') };
      }
      targets.push({ intent, id });
    }

    // ONE coherent read for the whole before-image, and the revision that names
    // it becomes the write's basis. Decision 0027: the plan and the commit
    // describe one state, or the commit is refused. Read inside, write outside
    // — `read` retries, so a commit in here could be replayed.
    const { before, basis, derived } = await deps.store.read(async ({ reader, revision }) => {
      const seen = new Map<string, unknown>();
      let denied = false;
      for (const { intent, id } of targets) {
        seen.set(`${id}\u0000${intent.role}`, await readRole(reader, manifest, id, intent.role));
        if (await deps.isDerivedRole?.(reader, id, manifest, intent.role)) denied = true;
      }
      return { before: seen, basis: revision, derived: denied };
    });
    if (derived) {
      return { ok: false, refusal: say('no-grant',
        'Not saved — this value is derived from canonical content and is read-only here.') };
    }

    let refusal: Refusal | undefined;
    const result = await deps.store.write(authority, basis, async (session) => {
      const staged: Instruction[] = [];
      for (const { intent, id } of targets) {
        const slot = slotOf.get(intent.role);
        if (!slot) {
          refusal ??= say('no-grant', `Not saved — no writable slot binds ${intent.role}.`);
          return;
        }
        const grant = await session.grant(slot, id);
        if (isRefusal(grant)) { refusal ??= fromStore(grant); return; }
        const instruction = instructionFor(grant, intent.role, intent.value);
        if ('code' in instruction) { refusal ??= instruction; return; }
        staged.push(instruction);
      }
      // Rule 3: staged first, applied second. Returning early above leaves the
      // session with nothing applied, which commits nothing at all.
      for (const instruction of staged) session.apply(instruction);
    });

    if (refusal) return { ok: false, refusal };
    if (!result.ok) return { ok: false, refusal: fromStore(result) };

    const patches: PropertyPatch[] = [];
    for (const { intent, id } of targets) {
      const was = before.get(`${id}\u0000${intent.role}`);
      patches.push({
        object: intent.object,
        role: intent.role,
        before: was,
        after: await readRole(deps.store, manifest, id, intent.role),
        // A write that CREATED the property changed the collection's coverage,
        // so the layout may now have a column it did not have. One that replaced
        // a value did not, and repainting for it would be churn.
        created: was === undefined,
      });
    }

    // A new edit ends whatever redo was still available: the snapshot each undone
    // commit would restore describes a state this write has just left behind.
    for (let i = history.length - 1; i >= 0; i -= 1) {
      if (history[i]!.undoneBy) history.splice(i, 1);
    }
    history.push({ journal: result.journal.id, scope: txn.scope });

    const last = txn.intents[txn.intents.length - 1]!;
    const node = grants.get(last.grant)?.node;
    // The ROLE travels with the node index, because the index does not survive
    // the repaint this write is about to cause: a write can change the
    // collection's coverage, the generator re-ranks, and the index means a
    // different field on the other side. Taken from the intent the store just
    // granted — the DOM is not asked, and is in any case already stale.
    deps.onWrote(node === undefined
      ? undefined
      : { object: last.object, node, role: last.role });
    return {
      ok: true,
      commit: { txn: txn.id, scope: txn.scope, at: txn.at, patches },
    };
  }

  /**
   * Undo and redo are the same operation against different journal entries: the
   * store restores a snapshot, and undoing an undo restores what the undo
   * replaced. Nothing is ever popped, which is what lets a second writer exist
   * (0005) without the stack becoming a lie.
   *
   * They pick their target from different orders, though, and that is the part
   * this used to get wrong. Undo takes the newest entry still standing. Redo
   * takes the most recent UNDO — `undoneAt`, not a position in `history`.
   * Scanning `history` newest-first for an undone entry finds the one undone
   * FIRST, because undo walks newest→oldest, so redo was FIFO where it must be
   * LIFO: undoing 'a' then 'b' and redoing twice handed back 'b' then 'a' and
   * finished on the wrong value.
   *
   * `redoTarget` in packages/runtime/src/edit.ts states the same LIFO rule over
   * a `Commit[]` ledger, and this deliberately does not call it: that model
   * keeps a redo reachable across a later edit, and redo here means restoring a
   * store snapshot taken before that edit — it would overwrite work the user did
   * after the undo. The rule is shared; the eligibility is not.
   */
  function nextStep(focused: UndoScopeId, want: 'undo' | 'redo'): HistoryEntry | undefined {
    let best: HistoryEntry | undefined;
    for (let i = history.length - 1; i >= 0; i -= 1) {
      const entry = history[i]!;
      if (!scopeContains(entry.scope, focused)) continue;
      if (want === 'undo') {
        if (!entry.undoneBy) return entry;                 // newest standing edit
        continue;
      }
      if (!entry.undoneBy) continue;
      if (!best || (entry.undoneAt ?? 0) > (best.undoneAt ?? 0)) best = entry;
    }
    return best;
  }

  async function step(
    focused: UndoScopeId, want: 'undo' | 'redo',
  ): Promise<WriteOutcome | undefined> {
    const entry = nextStep(focused, want);
    if (!entry) return undefined;

    const target = want === 'undo' ? entry.journal : entry.undoneBy!;
    // The inverse is assembled by the store from the journal entry, so there is
    // nothing here to plan from — but decision 0027 still refuses an unchecked
    // path, and an undo racing a later edit is exactly what it is protecting:
    // "an undo can mean something different after the store has moved".
    const out = await deps.store.undo(deps.editor, (await deps.store.snapshot()).revision, target);
    if (!out.ok) return { ok: false, refusal: fromStore(out) };

    if (want === 'undo') {
      undoClock += 1;
      entry.undoneBy = out.journal.id;
      entry.undoneAt = undoClock;
    } else {
      delete entry.undoneBy;
      delete entry.undoneAt;
    }

    deps.onWrote();
    return { ok: true, commit: nothingCommit(entry.scope) };
  }

  /**
   * Attach or detach, through the same three doors a value write goes through.
   *
   * The grant is looked up by GESTURE — `forRelation(node, mutation)` — because
   * a node slot may offer both and answering an attach with a detach grant
   * hands every slot that offers attach the authority to delete. That lookup is
   * the runtime's half; `session.relationGrant` is the store's, and it resolves
   * the public role to one private key or refuses. Both run, for the reason the
   * value path runs `checkIntent` twice: a store that trusts its caller is not
   * a boundary.
   *
   * The target never travels as an address. Detach carries the opaque token the
   * renderer took off the projection; attach hands the store an object and gets
   * a handle back. Neither can be assembled from typed text.
   */
  /**
   * The runtime grant and the store grant describe different slots.
   *
   * @Codex, on the version that had just made the role authoritative: *"a
   * programmatic caller can use `req.node` from one slot and `req.specId` from
   * the other … a target allowed for the first role can therefore be persisted
   * through the second role even when it is outside that role's declared
   * domain."*
   *
   * The same defect as `token ?? target` one level up, and it survived that
   * fix: making the role authoritative settled WHERE the role comes from and
   * left WHICH SLOT it describes chosen by a second, independent caller field.
   * `node` names the grant that is checked, `specId` names the authority that
   * writes, and nothing required them to be the same slot. **A check and a
   * write about two different things**, again.
   *
   * Refused rather than reconciled: the runtime grant has no `specId` to derive
   * the store slot from, and picking one of the two fields to believe would be
   * choosing which half of a contradictory request was the real one. A request
   * whose two halves disagree is malformed, and the honest answer is no.
   *
   * Unreachable in the product today — no demo pool declares two relations —
   * and reachable the day task 0075 lands, which is precisely why it is closed
   * now. A hole that opens when a planned feature arrives gets found by the
   * feature rather than by the fix.
   */
  const mismatched = (written: Role): Refusal => say('affordance-mismatch',
    `Not saved — this control does not write ${written}.`);

  async function relate(req: RelateRequest): Promise<WriteOutcome> {
    const scope: UndoScopeId = req.lens as UndoScopeId;
    const grants = installed.get(req.lens);
    if (!grants) {
      return { ok: false, refusal: say('grant-unknown', 'Not saved — this lens is not installed.') };
    }
    // KEPT, not discarded. The grant carries the authoritative role, and the
    // first version of the target-scope check below threw it away after a null
    // check and trusted a role the CALLER supplied instead. @Codex:
    //
    //   "A direct caller can send `role: undefined` (or omit it) and an
    //    out-of-scope target is written … a `relation.peer` link can pass
    //    `role: 'source'`; `scopeForRole('source')` is unconstrained, so an
    //    out-of-domain target is accepted and then written through the peer
    //    `specId`. `grants.forRelation(...)` already returns the authoritative
    //    `WriteGrant.role`."
    //
    // Making the caller's field required on `RelateSite` caught omission at one
    // UI construction site and left the boundary non-authoritative, which is
    // not the same thing. The field is GONE rather than merely distrusted: one
    // that nothing reads still reads like one that decides something.
    const grant = grants.forRelation(req.node, req.mutation);
    if (!grant) {
      return { ok: false, refusal: say('no-grant',
        `Not saved — this slot does not offer ${req.mutation}.`) };
    }
    const authority = await authorityFor(req.lens);
    if ('code' in authority) return { ok: false, refusal: authority };

    const source = deps.objectOf(req.object);
    if (!source) {
      return { ok: false, refusal: say('object-not-writable',
        'Not saved — this row is from a render that is no longer on screen.') };
    }

    // WHICH FIELD NAMES THE OBJECT, decided by the mutation rather than by
    // whichever one happens to be set.
    //
    // `relate` used to resolve `token ?? target`, and @Codex found what that
    // costs: *"this condition approves the explicit target, but the handle
    // selection below prioritizes `token`, so the link persists the unchecked
    // object. A link with only a token skips this check entirely as well."* A
    // check and a write about two different objects is worse than no check,
    // because the refusal path never runs and the caller is told it worked.
    //
    // The vocabulary already separates them and the code merely had not: a
    // token is the occurrence handle the renderer took off a projection, and it
    // names an EXISTING edge. Only `unlink` has one. So `link` names a target,
    // `unlink` names a token, and a request carrying the other one is refused
    // rather than silently preferred.
    //
    // `affordance-mismatch` rather than a new code: this is a malformed request
    // from a programmatic caller, not a state a person can reach — no gesture
    // in the renderer can produce it — so it does not need to be distinguished
    // in anything a user reads.
    //
    // Checked against a LOOSE view of the request. The union above makes these
    // shapes unrepresentable to a TypeScript caller, and that is exactly why
    // the runtime check cannot read the request through it: narrowed to
    // `LinkRequest`, `req.token` is `never` and the comparison is one the
    // compiler would call pointless. The shapes a type cannot reach are the
    // ones this is here for.
    const shape = req as { mutation: RelationMutation; token?: string; target?: ObjectId };
    if (shape.mutation === 'link' && shape.token !== undefined) {
      return { ok: false, refusal: say('affordance-mismatch',
        'Not saved — a link names the object to point at, not an existing reference.') };
    }
    if (shape.mutation === 'unlink') {
      if (shape.token === undefined) {
        return { ok: false, refusal: say('affordance-mismatch',
          'Not saved — removing a reference names the one to remove.') };
      }
      // The fourth shape, and the one a symmetric reading of the first three
      // would miss: an unlink carrying a target names an object where the
      // gesture needs an occurrence. Two references to one object are two
      // occurrences, so a target cannot say which is being removed.
      if (shape.target !== undefined) {
        return { ok: false, refusal: say('affordance-mismatch',
          'Not saved — removing a reference names the reference, not the object.') };
      }
    }

    // BEFORE the target is turned into a handle, and before any session opens.
    // A refusal that has already minted a handle is a refusal that had to be
    // remembered to be undone.
    //
    // Note what is NOT here: a condition on the role being present. It comes
    // from the grant, so it always is. Reading a missing role as "unconstrained"
    // is precisely how the first version could be switched off by a caller.
    if (shape.mutation === 'link' && deps.targetInScope) {
      if (shape.target === undefined) {
        return { ok: false, refusal: say('value-not-parsable',
          'Not saved — no target was chosen.') };
      }
      if (!await deps.targetInScope(grant.role, shape.target)) {
        return { ok: false, refusal: say('target-out-of-scope',
          'Not saved — that is not something this reference can point at.') };
      }
    }

    // Keyed on the MUTATION rather than on which field is set. After the two
    // refusals above the two are equivalent — a link never carries a token and
    // an unlink always does — so this is deliberately redundant and says so:
    // mutation-checked, swapping it back to `req.token !== undefined` changes
    // no behaviour and no test catches it, because nothing reaches it. It reads
    // as the rule rather than as a consequence of two guards above it, which is
    // what the version that resolved `token ?? target` failed to do.
    const handle = shape.mutation === 'unlink'
      ? deps.store.relationTargetFor(authority, shape.token!)
      : shape.target !== undefined
        ? deps.store.relationTarget(authority, shape.target)
        : say('value-not-parsable', 'Not saved — no target was chosen.');
    if ('code' in handle) {
      return { ok: false, refusal: 'ok' in handle ? fromStore(handle) : handle };
    }

    let refusal: Refusal | undefined;
    // Nothing was read to plan this: the handle came from the picker and the
    // mutation from the shape. A snapshot taken here is still the basis the
    // decision requires — "callers that intend a blind write still begin from a
    // snapshot; they do not opt out of concurrency control".
    const result = await deps.store.write(authority, (await deps.store.snapshot()).revision,
      async (session) => {
      // Branched rather than parameterised, because `relationGrant` is generic
      // in the mutation and a union argument returns a union grant — which no
      // `Instruction` arm accepts. The repetition is the type system insisting
      // that a link grant and an unlink grant are different things, which is
      // the same insistence decision 0016 makes in prose.
      if (shape.mutation === 'link') {
        const written = await session.relationGrant(req.specId, source, 'link');
        if (isRefusal(written)) { refusal ??= fromStore(written); return; }
        if (written.role !== grant.role) { refusal ??= mismatched(written.role); return; }
        session.apply({ mutation: 'link', grant: written, target: handle });
      } else {
        const written = await session.relationGrant(req.specId, source, 'unlink');
        if (isRefusal(written)) { refusal ??= fromStore(written); return; }
        if (written.role !== grant.role) { refusal ??= mismatched(written.role); return; }
        session.apply({ mutation: 'unlink', grant: written, target: handle });
      }
    });
    if (refusal) return { ok: false, refusal };
    if (!result.ok) return { ok: false, refusal: fromStore(result) };

    // An idempotent no-op writes no journal entry, so there is nothing to undo
    // and recording one would let a later undo remove a relation this gesture
    // never created. Decision 0016 asks for exactly that.
    const moved = (result.membership?.linked ?? 0) + (result.membership?.unlinked ?? 0) > 0;
    if (moved) {
      for (let i = history.length - 1; i >= 0; i -= 1) {
        if (history[i]!.undoneBy) history.splice(i, 1);
      }
      history.push({ journal: result.journal.id, scope });
    }
    deps.onWrote();
    return { ok: true, commit: nothingCommit(scope) };
  }

  return {
    relate,
    installLens(lens, grants) { installed.set(lens, grants); },
    /**
     * Grants go; authorities need no sweeping.
     *
     * This used to walk the authority cache evicting every string key that
     * began with the lens id — necessary while that cache was an ordinary `Map`
     * that would otherwise hold a `BindingAuthority` for a lens nobody could
     * name any more. The cache hangs off the SPEC OBJECT now, so an authority
     * is reachable only while the spec it was minted from is, and the render
     * loop dropping a regenerated spec drops it.
     *
     * There is nothing to enumerate and nothing to leak, which is the property
     * the previous comment claimed and the previous code did not have.
     */
    uninstallLens(lens) {
      installed.delete(lens);
    },
    apply,
    undo: (scope) => step(scope, 'undo'),
    redo: (scope) => step(scope, 'redo'),
  };
}
